import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';
import { useUser } from '../context/userContext';
import ChangeLanguage from '../components/ChangeLanguage';
import IOSDownload from '../components/IOSDownload';
import DropdownSelector from '../components/DropdownSelector';
import { isIos, isInStandaloneMode } from '../utils/helpers';

const LoginPage: React.FC = () => {
  const [isNewPassword, setIsNewPassword] = useState(false);
  const { t } = useTranslation();
  const { login, availableRoles, forgetPassword } = useUser();
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    email: '',
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = (): void => {
    if (deferredPrompt) {
      (deferredPrompt as any).prompt();
      (deferredPrompt as any).userChoice.then(() => {
        setDeferredPrompt(null);
      });
    }
  };

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      await login(credentials.username, credentials.password);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    try {
      await forgetPassword(credentials.email);
      setSuccess(t("successfulForgotPassword"));
      setIsLoading(false);
    } catch {
      setError(t('unSuccessful'));
      setIsLoading(false);
    }
  }

  const handleRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password || !selectedRole) return;
    try {
      await login(credentials.username, credentials.password, selectedRole);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  // Prepare options for DropdownSelector (as Record<string, string>)
  const roleOptions = availableRoles ? {
    '': t('role'),
    ...availableRoles.reduce((acc, role) => {
      acc[role] = t(`roles.${role}`);
      return acc;
    }, {} as Record<string, string>)
  } : {};

  return (
    <div className="loginPage">
      <img className="logo" src={logo} alt="Logo" />
      <div className="loginBox">
        {availableRoles ? (
          <>
            <form onSubmit={handleRoleSubmit}>
              <label className='loginText'>{t('selectRole')}</label>
              <DropdownSelector
                label=""
                options={roleOptions}
                value={selectedRole}
                onChange={setSelectedRole}
              />
              <button type="submit" style={{marginBottom: '40px'}}>{t('loginWithRole')}</button>
            </form>
          </>
        ) : isNewPassword ? (
          <>
            <form onSubmit={handleForgotPassword}>
                { success !== '' ?
                  <p>{t('successfulForgotPassword')}</p>
                  : <p className='loginText'>Kérlek add meg az e-mail címed!</p>
                }
              <input
                type="email"
                placeholder={t('email')}
                name="email"
                onChange={handleInputChange}
                value={credentials.email}
              />
              {error && <p className="error">{t(error)}</p>}
              <button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="spin" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="30 20" strokeLinecap="round"/>
                  </svg>
                ) : t('newPasswordButton')}
              </button>
            </form>
            <p className="link" onClick={() => setIsNewPassword(false)}>
              {t('back')}
            </p>
          </>
        ) : (
          <>
            <form onSubmit={handleLogin}>
              <input
                type="text"
                placeholder={t('usernameOrEmail')}
                name="username"
                onChange={handleInputChange}
              />
              <input
                type="password"
                placeholder={t('password')}
                name="password"
                onChange={handleInputChange}
              />
              {error && <p className="error">{t(error)}</p>}
              <p className='link' onClick={() => setIsNewPassword(true)}>{t('forgotPassword')}</p>
              <button type="submit">{t('loginButton')}</button>
            </form>
          </>
        )}

        <ChangeLanguage />
        {isIos() && !isInStandaloneMode() ? (
          <IOSDownload />
        ) : (
          deferredPrompt && (
            <button className="button" onClick={handleInstallClick}>
              {t('btnInstall')}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default LoginPage;
